import { prop } from '@ipfabric/utils/fp/prop';
import {
  type AppThemeState,
  useGlobalStateContextSelectors,
} from 'components/GlobalState/GlobalStateModel';
import { setGlobalAppTheme } from 'components/GlobalState/commands/setGlobalAppTheme';
import { type FC, type ReactNode, useEffect } from 'react';

declare global {
  interface Window {
    changeAppTheme?: (newTheme: AppThemeState) => void;
  }
}

export interface AppThemeProps {
  children: ReactNode;
}

export const AppTheme: FC<AppThemeProps> = (props) => {
  const globalStateCtxProps = useGlobalStateContextSelectors({
    theme: prop('user', 'theme'),
    dispatch: prop('dispatch'),
  });

  // TEMPORARY SOLUTION START
  // Currently, this is a temporary workaround for switching themes directly via the browser's dev tools.
  // It's intended for development purposes only. A more robust theme-switching mechanism is planned for
  // future implementation. It doesn't have NIM-XXXXX issue yet.
  // Once the permanent solution is in place, this code block, along with the `declare global` statement
  // at the start of this file, should be removed.
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      window.changeAppTheme = (newTheme: AppThemeState) => {
        globalStateCtxProps.dispatch(setGlobalAppTheme, { theme: newTheme });
      };
      return () => {
        // biome-ignore lint/performance/noDelete: intentional cleanup
        delete window.changeAppTheme;
      };
    }
    return undefined;
  }, [globalStateCtxProps.dispatch]);
  // TEMPORARY SOLUTION END

  useEffect(() => {
    if (typeof document === 'undefined') {
      return undefined;
    }

    const currentTheme = globalStateCtxProps.theme;
    if (!currentTheme) {
      return undefined;
    }

    const { classList } = document.body;
    classList.add(currentTheme);
    return () => {
      classList.remove(currentTheme);
    };
  }, [globalStateCtxProps.theme]);

  return <div className='h-full min-h-full'>{props.children}</div>;
};
